import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import BinatedLifecycleRevisionModal from "./BinatedLifecycleRevisionModal";

const imageUrlPattern = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;
const DEFAULT_MEASUREMENT_UNIT = "inch";
const DEFAULT_OUTPUT_FORMAT = "pdf";
const DEFAULT_PRIORITY = "Next Day";
const ALLOWED_MEASUREMENT_UNITS = ["mm", "cm", "inch", "px"];
const ALLOWED_OUTPUT_FORMATS = ["ai", "eps", "pdf", "svg", "png", "jpg", "jpeg", "psd", "dst", "emb", "pes", "zip"];
const ALLOWED_PRIORITIES = ["Same Day", "Next Day", "3 Days"];

const formatStatusLabel = (value) =>
  String(value || "submitted").replace(/_/g, " ").toUpperCase();

const getStatusChipColor = (status = "") => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "action_required") return "warning";
  if (normalized === "failed_or_cancelled" || normalized === "failed" || normalized === "cancelled") {
    return "error";
  }
  if (normalized === "ready" || normalized === "completed") return "success";
  if (normalized === "submitted") return "info";
  return "primary";
};

const buildStatusMessage = (item = {}) => {
  const normalized = String(item?.normalizedStatus || "").toLowerCase();
  const comments = String(item?.changeComments || "").trim();

  if (normalized === "action_required") {
    return "Binated has this artwork on Hold. Review the live Binated order for the exact hold reason.";
  }
  if (normalized === "ready") return comments || "Ready / RFU.";
  if (normalized === "processing") return comments || "Processing.";
  if (normalized === "completed") return comments || "Completed.";
  if (normalized === "failed_or_cancelled") return comments || "Failed or cancelled.";
  return comments || "Submitted.";
};

const buildAccountSourceMessage = (lifecycle = {}) => {
  const explicit = String(lifecycle?.accountSourceMessage || "").trim();
  if (explicit) return explicit;

  const accountSource = String(lifecycle?.accountSource || "").trim().toLowerCase();
  if (accountSource === "user") {
    return lifecycle?.accountSourceOwnedByViewer
      ? "Sent using your Binated account"
      : "Sent using a user Binated account";
  }
  return "Sent using the company Binated account";
};

const buildEliteCoreStatusLabel = (item = {}) => {
  const normalized = String(item?.normalizedStatus || "").toLowerCase();
  const rawStatus = String(item?.rawStatus || "").toLowerCase();
  const rawStage = String(item?.rawStage || "").toLowerCase();

  if (normalized === "processing") {
    if (
      rawStatus.includes("quality check") ||
      rawStatus.includes("qc completed") ||
      rawStatus.includes("qc in progress") ||
      rawStage.includes("rfu")
    ) {
      return "Processing / In QC";
    }
    return "Processing";
  }
  if (normalized === "action_required") return "Action Required";
  if (normalized === "awaiting_review") return "Awaiting Review";
  if (normalized === "revision_requested") return "Revision Requested";
  if (normalized === "ready") return "Ready";
  if (normalized === "completed") return "Completed";
  if (normalized === "failed_or_cancelled") return "Failed / Cancelled";
  return formatStatusLabel(normalized);
};

const buildPlacementLabel = (item = {}) => {
  const code = String(item?.placementCode || "").trim();
  const name = String(item?.placementName || "").trim();
  if (code && name && code !== name) return `${code} — ${name}`;
  return code || name || "-";
};

const getPreviewUrl = (item = {}) =>
  item?.productUrl || item?.fileUrls?.[0] || "";

const isImagePreview = (url = "") => imageUrlPattern.test(String(url || ""));

const toCsv = (value) =>
  Array.isArray(value) ? value.filter(Boolean).join(", ") : String(value || "");

const parseUrlList = (value) =>
  String(value || "")
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const tryParseUrl = (value = "") => {
  try {
    return new URL(String(value || "").trim());
  } catch {
    return null;
  }
};

const isSignedRemoteUrl = (parsedUrl) => {
  if (!parsedUrl) return false;
  return (
    parsedUrl.searchParams.has("X-Amz-Signature") ||
    parsedUrl.searchParams.has("X-Amz-Algorithm") ||
    parsedUrl.searchParams.has("Signature") ||
    parsedUrl.searchParams.has("Expires") ||
    parsedUrl.searchParams.has("token")
  );
};

const isLikelyPrivateStorageUrl = (value = "") => {
  const parsedUrl = tryParseUrl(value);
  if (!parsedUrl) return false;
  const host = String(parsedUrl.hostname || "").toLowerCase();
  if (!host) return false;
  const matchesPrivateHost =
    host.includes("amazonaws.com") ||
    host.includes(".s3.") ||
    host.startsWith("s3.") ||
    host.includes("storage.googleapis.com");
  return matchesPrivateHost && !isSignedRemoteUrl(parsedUrl);
};

const isBinatedFetchableUrl = (value = "") => {
  const parsedUrl = tryParseUrl(value);
  if (!parsedUrl) return false;
  if (!["http:", "https:"].includes(parsedUrl.protocol)) return false;
  return !isLikelyPrivateStorageUrl(value);
};

const resolveBinatedFetchableFileUrl = (file = {}) => {
  const candidates = [
    file?.publicUrl,
    file?.downloadUrl,
    file?.url,
    file?.filePath,
    file?.path,
    file?.Location,
    file?.previewUrl,
  ];
  return candidates
    .map((entry) => String(entry || "").trim())
    .find((entry) => entry && isBinatedFetchableUrl(entry)) || "";
};

const normalizeEnumValue = (value, allowedValues, fallbackValue) => {
  const trimmed = String(value || "").trim();
  return allowedValues.includes(trimmed) ? trimmed : fallbackValue;
};

const buildStatusItemUpdateForm = (item = {}) => ({
  placement_code: item?.placementCode || "",
  description: item?.description || "",
  reference: item?.reference || "",
  process_type: item?.processType || "",
  height: item?.height ?? "",
  width: item?.width ?? "",
  measurement_unit: normalizeEnumValue(item?.measurementUnit, ALLOWED_MEASUREMENT_UNITS, DEFAULT_MEASUREMENT_UNIT),
  colors: toCsv(item?.colors),
  url: "",
  output_file_format: normalizeEnumValue(
    String(item?.outputFileFormat || "").toLowerCase(),
    ALLOWED_OUTPUT_FORMATS,
    DEFAULT_OUTPUT_FORMAT
  ),
  garment: item?.garment || "",
  priority: normalizeEnumValue(item?.priority, ALLOWED_PRIORITIES, DEFAULT_PRIORITY),
});

export const shouldRenderBinatedLifecycleTimeline = (lifecycle = {}) => {
  const requestType = String(lifecycle?.requestType || "").trim().toLowerCase();
  const statusItems = Array.isArray(lifecycle?.statusItems) ? lifecycle.statusItems : [];
  return !(requestType === "design_service" && statusItems.length > 0);
};

function ArtworkItemCard({
  item,
  onReview,
  onRevise,
  canUpdateArtwork = false,
}) {
  const normalized = String(item?.normalizedStatus || "submitted").toLowerCase();
  const previewUrl = getPreviewUrl(item);
  const canPreview = previewUrl && isImagePreview(previewUrl);

  const renderAction = () => {
    if (normalized === "action_required") {
      if (!canUpdateArtwork) {
        return <Chip size="small" color="warning" label="Action Required" />;
      }
      return (
        <Button variant="contained" color="warning" onClick={() => onRevise?.(item)}>
          Update Artwork
        </Button>
      );
    }
    if (normalized === "ready") {
      return (
        <Button variant="outlined" onClick={() => onReview?.(item)}>
          Map / Review Proof
        </Button>
      );
    }
    if (normalized === "processing") {
      return (
        <Button variant="outlined" disabled>
          Waiting on Binated
        </Button>
      );
    }
    if (normalized === "completed") {
      return <Chip size="small" color="success" label="Completed" />;
    }
    return <Chip size="small" label={formatStatusLabel(normalized)} />;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
        <Box
          sx={{
            width: 82,
            height: 82,
            borderRadius: 1.5,
            border: "1px solid #e5e7eb",
            background: "#f8fafc",
            overflow: "hidden",
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          {canPreview ? (
            <Box
              component="img"
              src={previewUrl}
              alt={item?.productName || item?.productSku || "Artwork item"}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center", px: 1 }}>
              No preview
            </Typography>
          )}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 0.5 }}>
            <Chip size="small" label={item?.productSku || "SKU"} variant="outlined" />
            <Chip size="small" label={formatStatusLabel(normalized)} color={getStatusChipColor(normalized)} />
          </Stack>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {item?.productName || "Artwork Item"}
          </Typography>
          {item?.garment ? (
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              {item.garment}
            </Typography>
          ) : null}
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
            Placement: {buildPlacementLabel(item)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            Decoration: {item?.decorationType || "-"} {item?.processType ? `• ${item.processType}` : ""}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            Binated Status
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {item?.rawStatus || "-"} / {item?.rawStage || "-"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            Elite Core Status
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {buildEliteCoreStatusLabel(item)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            Due
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {item?.dueAt ? new Date(item.dueAt).toLocaleString() : "-"}
          </Typography>
        </Box>
      </Box>

      <Alert severity={normalized === "action_required" ? "warning" : normalized === "ready" ? "success" : "info"}>
        <Typography variant="caption">
          {buildStatusMessage(item)}
        </Typography>
      </Alert>

      {(item?.providerOrderId || item?.poUuid || item?.parentOrderUuid || item?.placementUuid) && (
        <Box sx={{ p: 1, borderRadius: 1.5, background: "#f8fafc" }}>
          <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5 }}>
            Provider Metadata
          </Typography>
          {item?.providerOrderId ? <Typography variant="caption" sx={{ display: "block" }}>so_uuid: {item.providerOrderId}</Typography> : null}
          {item?.poUuid ? <Typography variant="caption" sx={{ display: "block" }}>poUuid: {item.poUuid}</Typography> : null}
          {item?.parentOrderUuid ? <Typography variant="caption" sx={{ display: "block" }}>parentOrderUuid: {item.parentOrderUuid}</Typography> : null}
          {item?.placementUuid ? <Typography variant="caption" sx={{ display: "block" }}>placementUuid: {item.placementUuid}</Typography> : null}
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 0.25 }}>
        {renderAction()}
      </Box>
    </Paper>
  );
}

function ArtworkItemsSection({ statusItems = [], onReview, onRevise, canUpdateArtwork = false }) {
  if (!Array.isArray(statusItems) || statusItems.length === 0) {
    return (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Placement-level artwork details will appear here when Binated returns them.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Artwork Items
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
          gap: 1.25,
        }}
      >
        {statusItems.map((item, index) => (
          <ArtworkItemCard
            key={`${item?.placementUuid || item?.placementId || item?.productSku || "item"}-${index}`}
            item={item}
            onReview={onReview}
            onRevise={onRevise}
            canUpdateArtwork={canUpdateArtwork}
          />
        ))}
      </Box>
    </Box>
  );
}

export default function BinatedLifecycleStatusPanel({
  lifecycle,
  onRefresh,
  refreshing = false,
  timeline = null,
  onSubmitStatusItemUpdate,
  onUploadReplacementFiles,
  customerId = null,
  statusItemUpdating = false,
  children,
}) {
  const displayStatus = lifecycle?.aggregateStatus || lifecycle?.normalizedStatus || "submitted";
  const statusItems = Array.isArray(lifecycle?.statusItems) ? lifecycle.statusItems : [];
  const accountSourceMessage = buildAccountSourceMessage(lifecycle);
  const shouldShowTimeline = shouldRenderBinatedLifecycleTimeline(lifecycle);
  const hasActionRequired = statusItems.some(
    (item) => String(item?.normalizedStatus || "").toLowerCase() === "action_required"
  );
  const hasReadyItems = statusItems.some((item) =>
    ["ready", "completed"].includes(String(item?.normalizedStatus || "").toLowerCase())
  );
  const reviewSectionRef = useRef(null);
  const [placementRevisionOpen, setPlacementRevisionOpen] = useState(false);
  const [placementRevisionItem, setPlacementRevisionItem] = useState(null);
  const [placementRevisionNote, setPlacementRevisionNote] = useState("");
  const [placementUpdateForm, setPlacementUpdateForm] = useState({});
  const [replacementFiles, setReplacementFiles] = useState([]);
  const [replacementInternalFiles, setReplacementInternalFiles] = useState([]);
  const [placementSubmitError, setPlacementSubmitError] = useState("");

  const openPlacementRevision = (item) => {
    setPlacementRevisionItem(item || null);
    setPlacementRevisionNote("");
    setPlacementUpdateForm(buildStatusItemUpdateForm(item || {}));
    setReplacementFiles([]);
    setReplacementInternalFiles([]);
    setPlacementSubmitError("");
    setPlacementRevisionOpen(true);
  };

  const closePlacementRevision = () => {
    if (statusItemUpdating) return;
    setPlacementRevisionOpen(false);
    setPlacementRevisionItem(null);
    setPlacementRevisionNote("");
    setPlacementUpdateForm({});
    setReplacementFiles([]);
    setReplacementInternalFiles([]);
    setPlacementSubmitError("");
  };

  const handleReviewPlacement = () => {
    reviewSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePlacementFieldChange = (field, value) => {
    setPlacementSubmitError("");
    setPlacementUpdateForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePlacementSubmit = async () => {
    if (typeof onSubmitStatusItemUpdate !== "function" || !placementRevisionItem) return;
    const placementUuid = String(placementRevisionItem?.placementUuid || "").trim();
    if (!placementUuid) {
      setPlacementSubmitError("Missing Binated placement uuid.");
      return;
    }
    const measurementUnit = normalizeEnumValue(
      placementUpdateForm?.measurement_unit,
      ALLOWED_MEASUREMENT_UNITS,
      ""
    );
    const outputFileFormat = normalizeEnumValue(
      String(placementUpdateForm?.output_file_format || "").toLowerCase(),
      ALLOWED_OUTPUT_FORMATS,
      ""
    );
    const priority = normalizeEnumValue(
      placementUpdateForm?.priority,
      ALLOWED_PRIORITIES,
      ""
    );
    if (!measurementUnit || !outputFileFormat || !priority) {
      setPlacementSubmitError("Select a valid measurement unit, desired output format, and priority.");
      return;
    }
    let uploadedUrls = [];
    if (replacementFiles.length > 0) {
      if (typeof onUploadReplacementFiles !== "function") {
        setPlacementSubmitError("Replacement artwork upload is not available in this view.");
        return;
      }
      uploadedUrls = await onUploadReplacementFiles(replacementFiles, placementRevisionItem);
    }
    const manualUrls = parseUrlList(placementUpdateForm?.url);
    const internalUrls = replacementInternalFiles
      .map((entry) => String(entry?.filePath || entry?.url || "").trim())
      .filter(Boolean);
    const allCandidateUrls = [...new Set([...uploadedUrls, ...internalUrls, ...manualUrls])];
    const invalidUrls = allCandidateUrls.filter((url) => !isBinatedFetchableUrl(url));
    if (invalidUrls.length > 0) {
      setPlacementSubmitError(
        "One or more replacement artwork URLs are not public or signed for Binated to fetch. Remove private storage links before submitting."
      );
      return;
    }
    const replacementUrls = allCandidateUrls;
    if (!replacementUrls.length) {
      setPlacementSubmitError("Add at least one replacement artwork URL before submitting.");
      return;
    }
    setPlacementSubmitError("");
    try {
      await onSubmitStatusItemUpdate({
        statusItem: placementRevisionItem,
        data: {
          ...placementUpdateForm,
          measurement_unit: measurementUnit,
          output_file_format: outputFileFormat,
          priority,
          url: replacementUrls,
        },
        internalNote: placementRevisionNote,
      });
      closePlacementRevision();
    } catch (err) {
      setPlacementSubmitError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update Binated artwork."
      );
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Paper
        elevation={0}
        sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 2, background: "#f8fafc" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Binated Request Status
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              Request Type: {lifecycle?.requestType || "art"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              Submitted: {lifecycle?.submittedAt ? new Date(lifecycle.submittedAt).toLocaleString() : "-"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              Last Checked: {lifecycle?.updatedAt ? new Date(lifecycle.updatedAt).toLocaleString() : "-"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              {accountSourceMessage}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              size="small"
              label={formatStatusLabel(displayStatus)}
              color={getStatusChipColor(displayStatus)}
            />
            <Button variant="outlined" size="small" onClick={onRefresh} disabled={refreshing}>
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </Box>
        </Box>

        {hasActionRequired ? (
          <Alert severity="warning" sx={{ mt: 1.25 }}>
            {hasReadyItems
              ? "Some placements need action, but ready placements can still be reviewed or mapped individually."
              : "At least one placement needs action before the Binated request can fully proceed."}
          </Alert>
        ) : null}

        <ArtworkItemsSection
          statusItems={statusItems}
          onReview={handleReviewPlacement}
          onRevise={openPlacementRevision}
          canUpdateArtwork={typeof onSubmitStatusItemUpdate === "function"}
        />
      </Paper>

      <Divider />

      <Box ref={reviewSectionRef}>
        {shouldShowTimeline ? timeline : null}
        {children}
      </Box>

      <BinatedLifecycleRevisionModal
        open={placementRevisionOpen}
        title="Update Held Artwork"
        statusItem={placementRevisionItem}
        note={placementRevisionNote}
        onNoteChange={setPlacementRevisionNote}
        formData={placementUpdateForm}
        onFormChange={handlePlacementFieldChange}
        currentArtworkUrls={Array.isArray(placementRevisionItem?.fileUrls) ? placementRevisionItem.fileUrls : []}
        replacementFiles={replacementFiles}
        replacementInternalFiles={replacementInternalFiles}
        customerId={customerId}
        onFilesAdd={(nextFiles = []) => {
          setPlacementSubmitError("");
          setReplacementFiles((prev) => [...prev, ...nextFiles].filter(Boolean));
        }}
        onFileRemove={(idx) => {
          setPlacementSubmitError("");
          setReplacementFiles((prev) => prev.filter((_, itemIdx) => itemIdx !== idx));
        }}
        onInternalFileSelect={(file) => {
          const fileUrl = resolveBinatedFetchableFileUrl(file);
          if (!fileUrl) {
            setPlacementSubmitError(
              "The selected internal file does not have a public or signed URL that Binated can fetch."
            );
            return;
          }
          setPlacementSubmitError("");
          setReplacementInternalFiles((prev) => {
            if (prev.some((entry) => String(entry?.filePath || entry?.url || "").trim() === fileUrl)) {
              return prev;
            }
            return [
              ...prev,
              {
                filePath: fileUrl,
                fileName: file?.fileName || file?.name || "",
                previewUrl: String(file?.previewUrl || fileUrl).trim(),
              },
            ];
          });
          setPlacementUpdateForm((prev) => {
            const nextUrls = [...new Set([...parseUrlList(prev?.url), fileUrl])];
            return {
              ...prev,
              url: nextUrls.join("\n"),
            };
          });
        }}
        onInternalFileRemove={(idx) => {
          setPlacementSubmitError("");
          setReplacementInternalFiles((prev) => {
            const removed = prev[idx];
            const removedUrl = String(removed?.filePath || removed?.url || "").trim();
            const next = prev.filter((_, itemIdx) => itemIdx !== idx);
            if (removedUrl) {
              setPlacementUpdateForm((currentForm) => ({
                ...currentForm,
                url: parseUrlList(currentForm?.url)
                  .filter((url) => url !== removedUrl)
                  .join("\n"),
              }));
            }
            return next;
          });
        }}
        onClose={closePlacementRevision}
        onSubmit={handlePlacementSubmit}
        submitDisabledReason={typeof onSubmitStatusItemUpdate === "function"
          ? ""
          : "Placement updates are not available in this view."}
        submitError={placementSubmitError}
        submitLabel="Submit Update"
        submitting={statusItemUpdating}
        showFileUpload={false}
      />
    </Box>
  );
}
